import { getAddress, isAddress, zeroAddress, type Address } from "viem";

export type ShieldedPoolId = "01" | "usd0";

function envAddress(name: string): Address | null {
  const value = String(import.meta.env[name] ?? "").trim();
  if (!isAddress(value) || value.toLowerCase() === zeroAddress) return null;
  return getAddress(value);
}

function envUrl(name: string): string {
  return String(import.meta.env[name] ?? "").trim().replace(/\/+$/, "");
}

const legacyRelayerUrl = envUrl("VITE_RELAYER_URL");

export const PRIVACY_CONFIG = {
  canonicalServiceUrl: envUrl("VITE_PRIVATE_SERVICE_URL") || legacyRelayerUrl,
  trustedRelayerUrl: envUrl("VITE_TRUSTED_RELAYER_URL") || legacyRelayerUrl,
  relayerRegistryUrl: envUrl("VITE_RELAYER_REGISTRY_URL") || "/relayers.json",
  wasmUrl: envUrl("VITE_WASM_URL"),
  zkeyUrl: envUrl("VITE_ZKEY_URL"),
  treasuryShieldedAddress: String(import.meta.env.VITE_TREASURY_SHIELDED_ADDRESS ?? "").trim(),
  token01: envAddress("VITE_TOKEN_01_ADDRESS"),
  verifier: envAddress("VITE_VERIFIER_WRAPPER_ADDRESS"),
  feeController: envAddress("VITE_FEE_CONTROLLER_ADDRESS"),
  usd0: envAddress("VITE_USD0_ADDRESS"),
  usd0Gateway: envAddress("VITE_USD0_GATEWAY_ADDRESS"),
  pools: {
    "01": envAddress("VITE_POOL_01_ADDRESS"),
    usd0: envAddress("VITE_POOL_USD0_ADDRESS"),
  } satisfies Record<ShieldedPoolId, Address | null>,
} as const;

export function poolAddress(pool: ShieldedPoolId): Address {
  const address = PRIVACY_CONFIG.pools[pool];
  if (!address) throw new Error(`${pool === "01" ? "01" : "USD0"} pool is not configured`);
  return address;
}

export function requirePrivatePoolConfig(pool: ShieldedPoolId): void {
  poolAddress(pool);
  if (!PRIVACY_CONFIG.token01) throw new Error("01 token address is not configured");
  if (!PRIVACY_CONFIG.verifier) throw new Error("verifier address is not configured");
  if (!PRIVACY_CONFIG.feeController) throw new Error("fee controller is not configured");
  if (!PRIVACY_CONFIG.canonicalServiceUrl) throw new Error("canonical service URL is not configured");
  if (!PRIVACY_CONFIG.trustedRelayerUrl) throw new Error("trusted relayer URL is not configured");
  if (!PRIVACY_CONFIG.wasmUrl || !PRIVACY_CONFIG.zkeyUrl) throw new Error("proving asset URLs are not configured");
  if (!/^0x[0-9a-fA-F]{128}$/.test(PRIVACY_CONFIG.treasuryShieldedAddress)) {
    throw new Error("treasury shielded address is not configured");
  }
}

export function privatePoolConfigured(pool: ShieldedPoolId): boolean {
  try {
    requirePrivatePoolConfig(pool);
    return true;
  } catch {
    return false;
  }
}

export const usd0PrivacyConfigured = Boolean(PRIVACY_CONFIG.usd0);
